import { computed, ref, type Ref } from "vue";
import { supabase } from "../lib/supabase";
import { useToast } from "./useToast";
import {
  downloadGeneratedPdf,
  isDocumentGenerationTimeout,
  requestDocumentGeneration,
  waitForCompletedDocument,
} from "./useGeneratedDocument";
import { toDateObject } from "../utils/dateUtils";
import { captureError } from "../lib/sentry";
import { inspectieTypeLabels, type InspectieType } from "../../../../types";

/**
 * The inspectierapport PDF of one inspectie scope, as the portal shows it.
 *
 * The PDF is a `documents` row of type `rapport_<scope>` (version v1), rendered
 * by the printer through the documents queue. It is (re)generated whenever the
 * inspecteur e-mails the rapport, but the rapport can change afterwards and a
 * rapport released another way may never have had one. So every download goes
 * through one smart action: a current PDF downloads directly, anything else is
 * generated first and downloaded once the printer is done.
 *
 * "Current" is a heuristic: the PDF is outdated when the rapport, one of its
 * constateringen or one of its foto's changed after the PDF was completed.
 * Those are the timestamps the portal can read (and that the inspecteur app
 * maintains); a change elsewhere is caught the next time one of these moves.
 */

export type RapportPdfState =
  | "loading"
  /** Completed PDF, nothing changed since. */
  | "ready"
  /** Completed PDF, but the rapport changed after it was made. */
  | "outdated"
  /** No PDF yet: made on the first download. */
  | "missing"
  /** The printer is working on it (right now, or joined on page load). */
  | "generating"
  /** Still not done after the wait budget: finishes in the background. */
  | "queued"
  /** The last generation failed. */
  | "error";

export interface RapportPdfDocument {
  id: string;
  rapport_id: string;
  type: string;
  status: string;
  pdf_path: string | null;
  updated_at: string | null;
}

const DOCUMENT_SELECT = "id, rapport_id, type, status, pdf_path, updated_at";
const DOCUMENT_VERSION = "v1";
const IN_FLIGHT_STATUSES = ["pending", "processing", "html_only"];
/** An in-flight row older than this is no longer being worked on right now (matches useGeneratedDocument). */
const IN_FLIGHT_FRESH_MS = 180000;
const SUBJECT = "het rapport";

export const getRapportPdfDocumentType = (inspectieType: InspectieType) => `rapport_${inspectieType}`;

export const getRapportPdfKey = (rapportId: string, inspectieType: InspectieType) =>
  `${rapportId}:${getRapportPdfDocumentType(inspectieType)}`;

/** Same name the Documenten overview uses for a rapport PDF. */
export function getRapportPdfFileName(
  inspectieType: InspectieType,
  rapportNr: string | null | undefined,
  rapportId: string,
) {
  const scopeLabel = inspectieTypeLabels[inspectieType] || inspectieType;
  return `Inspectierapport ${scopeLabel} - ${rapportNr || rapportId}.pdf`;
}

/** When the printer finished the PDF (the row's last status update). */
export function getRapportPdfCompletedAt(document: RapportPdfDocument | null | undefined): Date | null {
  if (!document || document.status !== "completed" || !document.pdf_path) return null;
  return toDateObject(document.updated_at);
}

/** Rapport PDF rows keyed by getRapportPdfKey, for a batch of rapporten (RLS-scoped via can_select_document). */
export async function fetchRapportPdfDocuments(rapportIds: string[]): Promise<Map<string, RapportPdfDocument>> {
  const documents = new Map<string, RapportPdfDocument>();
  const ids = Array.from(new Set(rapportIds.filter(Boolean)));

  for (let offset = 0; offset < ids.length; offset += 100) {
    const { data, error } = await supabase
      .from("documents")
      .select(DOCUMENT_SELECT)
      .in("rapport_id", ids.slice(offset, offset + 100))
      .like("type", "rapport_%")
      .eq("version", DOCUMENT_VERSION);

    if (error) throw error;

    for (const row of (data || []) as RapportPdfDocument[]) {
      documents.set(`${row.rapport_id}:${row.type}`, row);
    }
  }

  return documents;
}

async function fetchRapportPdfDocument(rapportId: string, inspectieType: InspectieType) {
  const { data, error } = await supabase
    .from("documents")
    .select(DOCUMENT_SELECT)
    .eq("rapport_id", rapportId)
    .eq("type", getRapportPdfDocumentType(inspectieType))
    .eq("version", DOCUMENT_VERSION)
    .maybeSingle();

  if (error) throw error;
  return (data as RapportPdfDocument | null) ?? null;
}

async function fetchLatestTimestamp(
  query: PromiseLike<{ data: { updated_at: string | null } | null; error: unknown }>,
): Promise<Date | null> {
  try {
    const { data, error } = await query;
    if (error) return null;
    return toDateObject(data?.updated_at ?? null);
  } catch {
    return null;
  }
}

/**
 * Latest change to the rapport content the portal can see. Best effort: a
 * failed lookup counts as "no change", so an unreadable table never blocks a
 * download — it only means an outdated PDF may be served as is.
 */
export async function fetchRapportLastChange(rapportId: string): Promise<Date | null> {
  const [rapport, constatering, foto] = await Promise.all([
    fetchLatestTimestamp(supabase.from("rapporten").select("updated_at").eq("id", rapportId).maybeSingle()),
    fetchLatestTimestamp(
      supabase
        .schema("rapport_details")
        .from("constateringen")
        .select("updated_at")
        .eq("rapport_id", rapportId)
        .not("updated_at", "is", null)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ),
    fetchLatestTimestamp(
      supabase
        .schema("rapport_details")
        .from("fotos")
        .select("updated_at")
        .eq("rapport_id", rapportId)
        .not("updated_at", "is", null)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ),
  ]);

  const times = [rapport, constatering, foto].filter((date): date is Date => date !== null);
  if (times.length === 0) return null;
  return new Date(Math.max(...times.map((date) => date.getTime())));
}

/** State of a stored document row, without any client-side generation in progress. */
export function resolveRapportPdfState(
  document: RapportPdfDocument | null | undefined,
  lastChange: Date | null,
  now: number = Date.now(),
): Exclude<RapportPdfState, "loading"> {
  if (!document) return "missing";

  if (document.status === "completed") {
    const completedAt = getRapportPdfCompletedAt(document);
    if (!completedAt) return "missing";
    return lastChange && lastChange.getTime() > completedAt.getTime() ? "outdated" : "ready";
  }

  if (document.status === "error") return "error";

  if (IN_FLIGHT_STATUSES.includes(document.status)) {
    const updatedAt = toDateObject(document.updated_at);
    const fresh = updatedAt !== null && now - updatedAt.getTime() <= IN_FLIGHT_FRESH_MS;
    return fresh ? "generating" : "queued";
  }

  return "missing";
}

/**
 * Generate (or join an in-flight generation of) the rapport PDF and wait for
 * it. Throws DocumentGenerationTimeoutError when it outlasts the wait budget.
 */
async function generateRapportPdf(rapportId: string, inspectieType: InspectieType, onSlow?: () => void) {
  const documentId = await requestDocumentGeneration({
    type: getRapportPdfDocumentType(inspectieType),
    rapportId,
    subject: SUBJECT,
    version: DOCUMENT_VERSION,
  });
  return waitForCompletedDocument(documentId, { subject: SUBJECT, onSlow });
}

async function openGeneratedPdf(pdfPath: string, target: Window | null) {
  const { data, error } = await supabase.storage.from("generated-docs").createSignedUrl(pdfPath, 3600);
  if (error) throw error;
  if (!data?.signedUrl) throw new Error("Geen link beschikbaar voor het rapport");

  if (target) {
    target.location.href = data.signedUrl;
  } else {
    window.open(data.signedUrl, "_blank", "noopener");
  }
}

function reportFailure(toast: ReturnType<typeof useToast>, err: unknown) {
  if (isDocumentGenerationTimeout(err)) {
    // Not a defect: the queue keeps working on it, so keep it out of Sentry.
    toast.info("Het rapport wordt nog gemaakt. Zodra het klaar is, vind je het hier en onder Documenten.");
    return;
  }
  toast.error("Het rapport kon niet gedownload worden. Probeer het later opnieuw.", err);
}

/**
 * The rapport PDF of one scope on the rapport detail page: loads its state,
 * joins a generation that is already running, and offers the download actions.
 */
export function useRapportPdf(
  rapportId: Ref<string>,
  inspectieType: Ref<InspectieType | null>,
  rapportNr: Ref<string | null | undefined>,
) {
  const toast = useToast();
  const document = ref<RapportPdfDocument | null>(null);
  const lastChange = ref<Date | null>(null);
  const loading = ref(false);
  /** Client-side phase that overrides the stored row while we wait on the printer. */
  const phase = ref<"generating" | "queued" | "error" | null>(null);
  /** Whether the current generation was started by a click (and so ends in a download). */
  const userInitiated = ref(false);
  let loadToken = 0;

  const state = computed<RapportPdfState>(() => {
    if (phase.value) return phase.value;
    if (loading.value && !document.value) return "loading";
    return resolveRapportPdfState(document.value, lastChange.value);
  });

  const completedAt = computed(() => getRapportPdfCompletedAt(document.value));

  const fileName = computed(() =>
    inspectieType.value
      ? getRapportPdfFileName(inspectieType.value, rapportNr.value, rapportId.value)
      : "Inspectierapport.pdf",
  );

  async function load() {
    const id = rapportId.value;
    const type = inspectieType.value;
    if (!id || !type) return;

    const token = ++loadToken;
    loading.value = true;
    try {
      const [row, changed] = await Promise.all([fetchRapportPdfDocument(id, type), fetchRapportLastChange(id)]);
      if (token !== loadToken) return;
      document.value = row;
      lastChange.value = changed;
    } catch (err) {
      if (token !== loadToken) return;
      console.error("Kon de rapport-PDF niet laden:", err);
      captureError(err, { stage: "useRapportPdf.load", rapportId: id });
      document.value = null;
    } finally {
      if (token === loadToken) loading.value = false;
    }

    // A generation started elsewhere (the inspecteur sending the rapport, or a
    // click before a reload) is followed here, without a surprise download.
    if (
      token === loadToken &&
      !phase.value &&
      resolveRapportPdfState(document.value, lastChange.value) === "generating"
    ) {
      void follow(false);
    }
  }

  async function follow(download: boolean) {
    const id = rapportId.value;
    const type = inspectieType.value;
    if (!id || !type) return;

    const token = loadToken;
    phase.value = "generating";
    userInitiated.value = download;
    try {
      const done = await generateRapportPdf(id, type);
      if (download) await downloadGeneratedPdf(done.pdf_path, fileName.value, SUBJECT);
      if (token !== loadToken) return;
      phase.value = null;
      await load();
    } catch (err) {
      console.error("Rapport-PDF maken mislukt:", err);
      if (token !== loadToken) return;
      phase.value = isDocumentGenerationTimeout(err) ? "queued" : "error";
      if (download) reportFailure(toast, err);
    }
  }

  /** The primary action: download the current PDF, making a new one first when needed. */
  async function download() {
    if (state.value === "loading" || state.value === "generating") return;

    if (state.value === "ready" && document.value?.pdf_path) {
      try {
        await downloadGeneratedPdf(document.value.pdf_path, fileName.value, SUBJECT);
      } catch (err) {
        reportFailure(toast, err);
      }
      return;
    }

    await follow(true);
  }

  /** Outdated only: take the existing (older) PDF right away. */
  async function downloadCurrentVersion() {
    if (!document.value?.pdf_path) return;
    try {
      await downloadGeneratedPdf(document.value.pdf_path, fileName.value, SUBJECT);
    } catch (err) {
      reportFailure(toast, err);
    }
  }

  async function openInNewTab() {
    if (!document.value?.pdf_path) return;
    // Open the tab synchronously inside the click so popup blockers allow it.
    const target = window.open("", "_blank");
    try {
      await openGeneratedPdf(document.value.pdf_path, target);
    } catch (err) {
      target?.close();
      reportFailure(toast, err);
    }
  }

  async function refresh() {
    phase.value = null;
    await load();
  }

  return {
    state,
    document,
    completedAt,
    lastChange,
    userInitiated,
    load,
    refresh,
    download,
    downloadCurrentVersion,
    openInNewTab,
  };
}

/**
 * Rapport PDF downloads from the rapporten list, where the PDF state is only
 * known from the batch-loaded document rows: freshness is checked on click.
 */
export function useRapportPdfDownloads() {
  const toast = useToast();
  const busyKeys = ref<Set<string>>(new Set());

  function setBusy(key: string, busy: boolean) {
    const next = new Set(busyKeys.value);
    if (busy) next.add(key);
    else next.delete(key);
    busyKeys.value = next;
  }

  /** Downloads the rapport PDF, generating a new one when it is missing or outdated. Returns the fresh row, if any. */
  async function downloadRapportPdf(params: {
    rapportId: string;
    inspectieType: InspectieType;
    rapportNr: string | null | undefined;
    document: RapportPdfDocument | null | undefined;
  }): Promise<RapportPdfDocument | null> {
    const key = getRapportPdfKey(params.rapportId, params.inspectieType);
    if (busyKeys.value.has(key)) return null;

    const fileName = getRapportPdfFileName(params.inspectieType, params.rapportNr, params.rapportId);
    setBusy(key, true);
    try {
      if (params.document?.status === "completed" && params.document.pdf_path) {
        const lastChange = await fetchRapportLastChange(params.rapportId);
        if (resolveRapportPdfState(params.document, lastChange) === "ready") {
          await downloadGeneratedPdf(params.document.pdf_path, fileName, SUBJECT);
          return null;
        }
      }

      const done = await generateRapportPdf(params.rapportId, params.inspectieType);
      await downloadGeneratedPdf(done.pdf_path, fileName, SUBJECT);
      return await fetchRapportPdfDocument(params.rapportId, params.inspectieType);
    } catch (err) {
      console.error("Rapport-PDF downloaden mislukt:", err);
      reportFailure(toast, err);
      return null;
    } finally {
      setBusy(key, false);
    }
  }

  return { busyKeys, downloadRapportPdf };
}
